/*
    this component was already existing in the codebase,
    we've made changes into it so we can reuse it on the new search overview page
    after we will finish the migration we can remove the old component
    and move this one to the correct location
*/

import type { FunctionComponent } from "react";
import React, { useCallback, useEffect, useMemo, useReducer, useRef } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import _ from "lodash";

import { TabPanel } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import { chosenSites as chosenSitesService } from "app/common/services/chosenSites";
import {
    ErrorComponent,
    NoDataComponent,
} from "app/pages/website-analysis/traffic-sources/search/tabs/SearchOverviewGraph/Components/EmptyState";
import {
    transformCompareMMXData,
    transformSingleMMXData,
} from "app/pages/website-analysis/traffic-sources/search/tabs/SearchOverviewGraph/Helpers/SearchOverviewGraphDataParsers";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import { DefaultFetchService } from "app/services/fetchService";
import { allTrackers } from "app/services/track/track";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import CostPerVisitChart from "../../tabs/SearchOverviewGraph/Components/cost-per-visit-chart/CostPerVisitChart";
import PpcSpendChart from "../../tabs/SearchOverviewGraph/Components/ppc-spend-chart/PpcSpendChart";
import { TabsComponent } from "../../tabs/SearchOverviewGraph/Components/TabsComponent";
import { TrafficShareGraph } from "../../tabs/SearchOverviewGraph/Metrics/TrafficShareGraph";
import { useActionCreators } from "../../tabs/SearchOverviewGraph/SearchOverviewGraphActions";
import { searchOverviewContext } from "../../tabs/SearchOverviewGraph/SearchOverviewGraphContext";
import type {
    ICategory } from "../../tabs/SearchOverviewGraph/SearchOverviewGraphReducer";
import {
    initState,
    searchOverviewGraphReducer,
} from "../../tabs/SearchOverviewGraph/SearchOverviewGraphReducer";
import { Title, WidgetsOverviewWrapper } from "../styledComponents";
import { SearchOverviewTexts } from "../texts";

import { GraphTabHeader } from "./Components/GraphTabHeader";
import { SearchOverviewGraphContainer, TabContentStyle } from "./Components/StyledComponents";
import { FiltersRow } from "./FiltersRow";
import {
    ORGANIC_PAID_CATEGORY,
    BRANDED_NON_BRANDED_CATEGORY,
    SEARCH_TYPE_CATEGORY,
    timeGranularityList,
    MMX_ORGANIC_PAID,
    searchTrafficGraphTabsConfig,
} from "./Helpers/SearchOverviewGraphConfig";
import type { IGetDataResponse } from "./types";

interface IQueryParams {
    country: string;
    includeSubDomains: boolean;
    keys: string | string[];
    timeGranularity?: string;
    webSource: string;
    from: string;
    to: string;
    isWindow: boolean;
    latest?: boolean;
}

const SearchOverviewGraphInner: FunctionComponent = () => {
    const { params } = useCurrentStateAndParams();
    const { key } = params;

    const isCompare = key.split(",").length > 1;

    const getSelectedMetricTab = () => {
        if (!params.selectedSingleOverviewGraphTab) {
            return searchTrafficGraphTabsConfig[0];
        }

        const foundTab = searchTrafficGraphTabsConfig.find((tab) => tab.name === params.selectedSingleOverviewGraphTab);

        return foundTab ?? searchTrafficGraphTabsConfig[0];
    };

    const isDesktop = params.webSource === devicesTypes.DESKTOP;
    const isMobileWeb = params.webSource === devicesTypes.MOBILE;
    const isTotal = params.webSource === devicesTypes.TOTAL;

    const [state, dispatch] = useReducer(
        searchOverviewGraphReducer,
        {
            ...params,
            isSingle: !isCompare,
            isDesktop,
            isMobileWeb,
            isTotal,
            selectedMetricTab: getSelectedMetricTab(),
        } as unknown,
        initState,
    );
    // IMPORTANT: Expose all needed actions.
    const {
        fetchDataStart,
        fetchDataEnd,
        saveData,
        setActiveTab,
        setError,
        toggleLegendItem,
        selectChannel,
        resetChannelAndCategory,
        setCategory, // Needed for single-mode category changes
        setDataType,
        setTimeGranularity,
    } = useActionCreators(dispatch);

    const {
        selectedMetricTab,
        country,
        includeSubDomains,
        keys,
        granularity,
        webSource,
        from,
        to,
        isWindow,
        category,
        isLoading,
        isError,
        channel,
        rawData,
        totals,
    } = state;

    const fetchService = DefaultFetchService.getInstance();

    const queryParams: IQueryParams = useMemo(() => {
        return {
            country,
            includeSubDomains,
            keys,
            timeGranularity: timeGranularityList.monthly.name,
            webSource,
            from,
            to,
            isWindow,
            latest: undefined,
        };
    }, [country, includeSubDomains, keys, webSource, from, to, isWindow]);

    const Metrics = [TrafficShareGraph, PpcSpendChart, CostPerVisitChart];

    const getEndPoint = (cat: ICategory) => {
        switch (cat.id) {
            case ORGANIC_PAID_CATEGORY.id:
                return "widgetApi/TrafficSourcesSearch/SearchOrganicPaidOverview/BarChart";
            case BRANDED_NON_BRANDED_CATEGORY.id:
                return "widgetApi/TrafficSourcesSearchV2/BrandedKeywords/BarChart";
            case SEARCH_TYPE_CATEGORY.id:
                return "widgetApi/TrafficSourcesSearch/SearchTrafficByChannel/BarChart";
            case MMX_ORGANIC_PAID:
                return "widgetApi/MarketingMixTotal/TrafficSourcesOverview/BarChart";
            default:
                return "";
        }
    };

    const getData = useCallback(
        (cat: ICategory, customParams = queryParams): Promise<IGetDataResponse> => {
            const endPoint = getEndPoint(cat);
            return fetchService.get(endPoint, customParams);
        },
        [fetchService, queryParams],
    );

    const getMetricTotals = async (dataPromise: Promise<any>) => {
        if (isCompare) {
            return null;
        }
        const res = await dataPromise;
        return searchTrafficGraphTabsConfig.reduce((acc, { name }) => {
            return {
                ...acc,
                [name]: res?.Data?.[name]?.Data?.Total?.["All Channels"],
            };
        }, {});
    };

    const fetchDataRaceConditionProtected = useRef<number>(0);

    /**
     * Multi‑fetch for non‑MMX categories (OrganicPaid, BrandedNonBranded, SearchType)
     */
    const fetchNonMMXData = async (timeStamp: number) => {
        try {
            fetchDataStart();

            // Build query params.
            const monthlyQParams = { ...queryParams, timeGranularity: timeGranularityList.monthly.name };

            // Create the promise only for the active category.
            let activePromise: Promise<any>;
            if (category.id === ORGANIC_PAID_CATEGORY.id) {
                activePromise = getData(ORGANIC_PAID_CATEGORY, monthlyQParams);
            } else if (category.id === BRANDED_NON_BRANDED_CATEGORY.id) {
                activePromise = getData(BRANDED_NON_BRANDED_CATEGORY, monthlyQParams);
            } else {
                activePromise = getData(SEARCH_TYPE_CATEGORY, monthlyQParams);
            }

            // Only use the active promise (and get totals based on it).
            const results = await Promise.allSettled([activePromise, getMetricTotals(activePromise)]);

            if (timeStamp !== fetchDataRaceConditionProtected.current) {
                return;
            }

            const [mainResponseResult, totalsResult] = results;

            // Ensure mainResponse was fulfilled
            if (mainResponseResult.status !== "fulfilled") {
                setError();
                return;
            }

            const mainResponse = mainResponseResult.value;
            const rawMain = mainResponse?.Data ? mainResponse.Data : mainResponse;
            const metricName = selectedMetricTab.name;
            if (rawMain && rawMain.Data) {
                rawMain[metricName] = rawMain.Data;
            }

            // Safely extract totals if fulfilled, otherwise null.
            const totalsValue = totalsResult.status === "fulfilled" ? totalsResult.value : null;

            saveData(
                {
                    ...rawMain,
                    enrichedData: {},
                },
                totalsValue,
            );
        } catch (error) {
            setError();
        } finally {
            fetchDataEnd();
        }
    };

    const fetchMMXData = async (timeStamp: number) => {
        try {
            fetchDataStart();
            const response = await getData(category, { ...queryParams, latest: undefined });
            if (timeStamp !== fetchDataRaceConditionProtected.current) {
                return;
            }
            const transformed = !isCompare
                ? transformSingleMMXData(response, selectedMetricTab)
                : transformCompareMMXData(response, selectedMetricTab);
            saveData(transformed?.Data ?? response, null);
        } catch (error) {
            setError();
        } finally {
            fetchDataEnd();
        }
    };

    useEffect(() => {
        const timeStamp = Date.now();
        fetchDataRaceConditionProtected.current = timeStamp;

        if (category.id === MMX_ORGANIC_PAID) {
            fetchMMXData(timeStamp);
        } else {
            fetchNonMMXData(timeStamp);
        }
    }, [queryParams, category, channel, granularity, selectedMetricTab]); // eslint-disable-line react-hooks/exhaustive-deps

    const graphTabs = useMemo(() => {
        if (!isCompare && totals) {
            return searchTrafficGraphTabsConfig.map((tab) => ({
                ...tab,
                value: totals[tab.name],
            }));
        }
        return searchTrafficGraphTabsConfig;
    }, [totals, isCompare]);

    const isNoData = useCallback(() => {
        if (isLoading) {
            return false;
        }
        if (!rawData) {
            return true;
        }

        if (!isCompare) {
            return !rawData?.[selectedMetricTab.name]?.Data?.Total?.["All Channels"];
        }
        const allChannelsTotal = rawData?.[selectedMetricTab.name]?.["All Channels"]?.Total ?? {};
        return _.sum(Object.values(allChannelsTotal)) <= 0;
    }, [isLoading, isCompare, rawData, selectedMetricTab]);

    // Expose all required actions in context.
    const finalContext = useMemo(() => {
        return {
            ...state,
            actions: {
                fetchDataStart,
                fetchDataEnd,
                saveData,
                setActiveTab,
                setError,
                toggleLegendItem,
                selectChannel,
                resetChannelAndCategory,
                setCategory,
                setDataType,
                setTimeGranularity,
            },
            getSiteColor: chosenSitesService.getSiteColor,
            getData,
            chosenSites: chosenSitesService.get(),
            filtersRow: <FiltersRow />,
            emptyState: isError ? <ErrorComponent /> : isNoData() ? <NoDataComponent isSingle={!isCompare} /> : null,
        };
    }, [
        state,
        isError,
        isNoData,
        getData,
        fetchDataStart,
        fetchDataEnd,
        saveData,
        setActiveTab,
        setError,
        toggleLegendItem,
        selectChannel,
        resetChannelAndCategory,
        setCategory,
        setDataType,
        setTimeGranularity,
        isCompare,
    ]);

    const setSelectedTab = useCallback(
        (selectedTab) => {
            setActiveTab(selectedTab);
            allTrackers.dispatchTrackEvent("Metric Button", "click", `Over Time Graph/${selectedTab.eventName}`);

            const urlParams = {
                selectedSingleOverviewGraphTab: selectedTab.name,
            };

            queueMicrotask(() => {
                proNavigator.applyUpdateParams(urlParams, { reload: false });
            });
        },
        [setActiveTab],
    );

    return (
        <searchOverviewContext.Provider value={finalContext as any}>
            <WidgetsOverviewWrapper>
                <Title>{translate(SearchOverviewTexts.performance)}</Title>
                <SearchOverviewGraphContainer>
                    <GraphTabHeader
                        isSingle={!isCompare}
                        queryParams={queryParams}
                        params={params}
                    />
                    <div ref={state.chartRef}>
                        <TabsComponent
                            setSelected={setSelectedTab}
                            selectedTab={selectedMetricTab}
                            tabs={graphTabs}
                        >
                            {Metrics.map((MetricComponent, index) => (
                                <TabPanel key={index}>
                                    <TabContentStyle>
                                        <MetricComponent />
                                    </TabContentStyle>
                                </TabPanel>
                            ))}
                        </TabsComponent>
                    </div>
                </SearchOverviewGraphContainer>
            </WidgetsOverviewWrapper>
        </searchOverviewContext.Provider>
    );
};

export const SearchOverviewGraphConnected = React.memo(SearchOverviewGraphInner);
